/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

/** Ceiling for `pdt` (page dwell time): 5 minutes in seconds. */
export const PDT_MAX_SECONDS = 300;

/**
 * Page dwell time in whole seconds since navigation start, capped at
 * PDT_MAX_SECONDS. Deliberately LIVE (computed at each emission point, never
 * snapshot-replayed): the select payload and a later impression may report
 * different values — downstream training and pre-inference processing bucket
 * the raw seconds, absorbing the divergence.
 */
export const getPageDwellTimeSeconds = (): number => {
  try {
    return Math.min(Math.floor(performance.now() / 1000), PDT_MAX_SECONDS);
  } catch (error) {
    return 0;
  }
};

/**
 * One-shot page-engagement flag: has the user scrolled, clicked or tapped
 * this page view? Two `{once, passive}` listeners flip the flag and
 * self-remove, so there is zero steady-state cost — no throttling, no
 * per-placement listeners.
 */
class PageEngagement {
  private static instance: PageEngagement | null = null;
  private engaged: boolean = false;
  private initialised: boolean = false;

  private constructor() {}

  public static getInstance(): PageEngagement {
    if (!PageEngagement.instance) {
      PageEngagement.instance = new PageEngagement();
    }
    return PageEngagement.instance;
  }

  public init(): void {
    if (this.initialised) {
      return;
    }
    this.initialised = true;
    const markEngaged = () => {
      this.engaged = true;
    };
    try {
      // pointerdown covers mouse clicks and touch taps.
      window.addEventListener('scroll', markEngaged, { once: true, passive: true });
      window.addEventListener('pointerdown', markEngaged, { once: true, passive: true });
    } catch (error) {
      // Non-browser environment; the flag simply stays false.
    }
  }

  public hasEngaged(): boolean {
    return this.engaged;
  }

  // This method is used for testing purposes only
  public _reset(): void {
    this.engaged = false;
    this.initialised = false;
  }
}

export { PageEngagement };
